'use strict';
import { fh_add, fh_mul, fh_polygon } from '@acenv/fh-3d-viewer';
import { cn_building } from '../../model/cn_building';
import { cn_nomenclature, export_layers_and_zpsos, find_zone, push_cn_nomenclatures_zones } from '../cn_nomenclature';
import { cn_plugin_option } from '../cn_plugin_option';
import { cn_constant , PIPE_FLUID_LIST, PIPE_SHAPE_LIST } from '../../model/cn_model_constants';
import { extension_instance } from '../../extension/cn_extension';
import { cn_storey } from '../../model/cn_storey';
import { cn_zone } from '../../model/cn_zone';
import { cnx_clone, cnx_dist } from '../cn_utilities';
//***********************************************************************************
//***********************************************************************************
//**** Build nomenclatures
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
/**
 * Builds pipes nomenclatures for the building
 * @param {cn_building} building
 * @param is_exterior
 * @returns {cn_nomenclature[]}
 */

export function cn_nomenclature_pipes(building, is_exterior = false) {
    const output = [];
    building.compute_altitudes();
    output.push(new cn_nomenclature('ID').withVirtual(true));
    output.push(new cn_nomenclature('Nom'));
    output.push(new cn_nomenclature('Identifiant'));
    if (!is_exterior) {
        output.push(new cn_nomenclature('Niveau'));
        output.push(new cn_nomenclature('Index niveau'));
    }
    push_cn_nomenclatures_zones(output, true);
    output.push(new cn_nomenclature('Espace'));
    output.push(new cn_nomenclature('Matériau'));
    output.push(new cn_nomenclature('Fluide'));
    output.push(new cn_nomenclature('Forme'));
    output.push(new cn_nomenclature('Diamètre').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Épaisseur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Largeur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Orientation').withNumberDefinition('°', 0));
    output.push(new cn_nomenclature('Hauteur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Longueur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Calques').withVisibility(false));
    if (cn_plugin_option.zpsos) {
        output.push(new cn_nomenclature('ZPSOs').withVisibility(false));
    }
    output.push(new cn_nomenclature('Longueur par espace (m)').withVisibility(false));

    const zones = building.zones;

    if (is_exterior) {
        /*** exterior */
        populate_nomenclature_pipes(building.exterior, zones, output);
    } else {
        building.storeys.length && building.storeys.forEach((storey) => {
            populate_nomenclature_pipes(storey, zones, output);
        });
    }
    return output;
}

/**
 * Builds pipes nomenclatures for the given storey
 * @param {any[]} output
 * @param {{[property: string]: cn_zone[]}} zones
 * @param {cn_storey} storey
 */
function populate_nomenclature_pipes(storey, zones, output) {
    const scene = storey.scene;
    scene.pipes.length && scene.pipes.forEach((item) => {
        const element_type = item.element_type;
        let k = 0;

        //*** ID */
        output[k].values.push(item.create_id_for_bbp(storey));
        k++

        //*** Type */
        output[k].values.push(element_type.get_label());
        k++

        //*** identifyer */
        output[k].values.push(item.get_readable_id(storey));
        k++

        //*** storey */
        if (!storey.exterior) {
            output[k].values.push(storey.get_storey_name());
            k++
            output[k].values.push(storey.get_storey_index_in_building());
            k++
        }

        let p0 = cnx_clone(item.vertices[0]);
        p0[2] += storey.altitude;
        let p1 = cnx_clone(item.vertices[1]);
        p1[2] += storey.altitude;
        const space_distances = storey.building.get_spaces_on_3d_path(p0, p1).filter(sd => !!sd.space);

        //*** zone */
        extension_instance.zone.get_zones_tools().forEach(tool_zone => {
            const zones_to_display = new Set();
            space_distances.forEach(sd => {
                const zone = find_zone(zones[tool_zone.property], sd.space.ID, sd.storey.ID);
                if (zone) {
                    zones_to_display.add(zone);
                }
            })
            output[k].values.push(Array.from(zones_to_display).join(', '));
            k++;
        });

        //*** space */
        output[k].values.push(space_distances.map(sd => sd.space.get_name(sd.storey)).join(', '));
        k++

        //*** material */
        output[k].values.push(element_type.get_material_label());
        k++

        //*** fluid */
        output[k].values.push(cn_constant.code_to_label(element_type.fluid, PIPE_FLUID_LIST));
        k++

        //*** shape */
        output[k].values.push(cn_constant.code_to_label(element_type.shape, PIPE_SHAPE_LIST));
        k++

        if (element_type.shape === 'circular') {
            //*** diameter */
            output[k].values.push(element_type.thickness);
            k++
            //*** Thickness */
            output[k].values.push('');
            k++
            //*** Width */
            output[k].values.push('');
            k++
        } else {
            //*** diameter */
            output[k].values.push('');
            k++
            //*** Thickness */
            output[k].values.push(element_type.thickness);
            k++
            //*** Width */
            output[k].values.push(element_type.width);
            k++
        }

        let a = item.vertices[0]
        let b = item.vertices[1]

        //*** Orientation */
        let orientation = '';
        if (a[0] === b[0]) {
            orientation = '0';
        }
        output[k].values.push(orientation);
        k++

        //*** height */
        if (a[0] === b[0]) {
            // Vertical
            output[k].values.push(a[2] < b[2] ? b[2] : a[2]);
        } else {
            output[k].values.push(a[2] <= b[2] ? (a[2].toFixed(2) + ' / ' + b[2].toFixed(2)) : (b[2].toFixed(2) + ' / ' + a[2].toFixed(2)));
        }
        k++

        //*** length
        let distance = Number(cnx_dist(a, b).toFixed(2));
        output[k].values.push(distance);
        k++;

        //*** layers & zpsos  */
        k = export_layers_and_zpsos(storey, item, element_type, output, k);

        output[k].values.push(space_distances.map(sd => `${sd.space.get_name(storey)}: ${sd.distance.toFixed(2)}`).join(' / '));
        k++;
    });
}
